import { CountryCode, CountryName, TranslationKey } from '@dayetopia/library';
import { isEmailMfaEnabled } from '@utils/environment';

export const countriesInEULocale = [
  CountryCode.NL,
  CountryCode.BE,
  CountryCode.IT,
  CountryCode.ES,
  CountryCode.PT,
  CountryCode.FR,
  CountryCode.SE,
  CountryCode.DK,
  CountryCode.NO,
  CountryCode.FI,
  CountryCode.AT,
  CountryCode.CH
];

// for e.g. -US would show in all apart from US, and US,GB would show only in both US & GB, works with , separators too
export function showInCountryCondition(showInCountries?: string | null, country?: CountryCode | string): boolean {
  if (!country || !showInCountries?.length) {
    return true;
  }

  const showInCountriesArray = showInCountries
    ?.split(',')
    .map((c) => c.trim())
    .filter((c) => !!c);

  if (!showInCountriesArray?.length) {
    return true;
  }

  let include = false;
  let exclude = false;

  showInCountriesArray.forEach((showInCountrySingle) => {
    if (showInCountrySingle.startsWith('-')) {
      if (showInCountrySingle.slice(1) === country) {
        exclude = true;
      }
    } else if (showInCountrySingle === country) {
      include = true;
    }
  });

  if (exclude) {
    return false;
  }

  return include || showInCountriesArray.every((c) => c.startsWith('-'));
}

/**
 * Filters an array of items based on the specified country.
 *
 * @template T - The type of the items.
 * @param {Array<T>} menuItems - The array of items to filter.
 * @param {CountryCode | string | undefined} country - The country code or name to filter by.
 * @returns {Array<T>} The filtered array of items that match the country condition.
 */
export function filterShowInCountries<T>(menuItems: Array<T>, country: CountryCode | string | undefined): Array<T> {
  return (
    menuItems?.filter((item: any) => {
      const showInCountries = item?.show_in_countries?.text || item?.show_in_countries;

      return showInCountryCondition(showInCountries, country);
    }) || []
  );
}

/**
 * We have a record of countries that are no longer in the EU locale,
 * have their respective locales,
 * but still have services available in English.
 *
 */
export const countriesNoLongerInEULocale = [CountryCode.IE, CountryCode.DE, CountryCode.BG];

export const isCountryInEULocale = (country: CountryCode) => {
  return countriesInEULocale.includes(country);
};

export const isCountryIsNoLongerInEULocale = (country: CountryCode) => {
  return countriesNoLongerInEULocale.includes(country);
};

/**
 * Gets the CountryCode for a given country name
 * @param countryName The full name of the country (e.g. "United Kingdom")
 * @returns The corresponding CountryCode (e.g. "GB") or undefined if not found
 */
export function getCountryCodeByName(countryName: string): CountryCode | undefined {
  const countryNameToCode = Object.entries(CountryName).reduce((acc, [code, name]) => {
    acc[name] = code as CountryCode;
    return acc;
  }, {} as Record<string, CountryCode>);

  return countryNameToCode[countryName];
}

/**
 * Gets the translation key for a country name based on country code
 * @param countryCode The country code (e.g., 'GB', 'US', 'BG')
 * @returns The translation key for the country name (e.g., 'countries.GB')
 */
export function getCountryNameTranslationKey(countryCode: string | undefined): TranslationKey {
  if (!countryCode) return '' as TranslationKey;
  return `countries.${countryCode}` as TranslationKey;
}

/**
 * Countries where treatment requests are not available
 */
const countriesWithoutTreatmentRequests: CountryCode[] = [CountryCode.IE, CountryCode.BG];

/**
 * Checks if treatment requests are available for a given country
 * @param country The country code to check
 * @returns true if treatment requests are available in this country
 */
export function hasTreatmentRequests(country: CountryCode | string | undefined): boolean {
  if (!country) return true;
  return !countriesWithoutTreatmentRequests.includes(country as CountryCode);
}

/**
 * Countries without specialists available
 */
const countriesWithoutSpecialists: CountryCode[] = [];

/**
 * Checks if a country has specialists available
 * @param country The country code to check
 * @returns true if specialists are available in this country
 */
export function hasSpecialists(country: CountryCode | string | undefined): boolean {
  if (!country) return true;
  return !countriesWithoutSpecialists.includes(country as CountryCode);
}

/**
 * Checks if email MFA should be available for a given locale.
 * Currently limited to Bulgarian locale (en-bg) behind a feature flag.
 * @param locale The locale string (e.g., 'en-bg')
 * @returns true if email MFA is available for this locale
 */
export function hasEmailMfa(locale: string | undefined): boolean {
  if (!isEmailMfaEnabled()) return false;
  return locale === 'en-bg';
}
